// src/pages/MiniWidgets.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { request } from "../../utils/request";
import "./dashboard.scss";

const MiniWidgets = () => {
  const [groupCount, setGroupCount] = useState(0);
  const [policiesCount, setPoliciesCount] = useState(0);
  const [whitelistCount, setWhitelistCount] = useState(0);
  const [activeSessionsCount, setActiveSessionsCount] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const grpData = await request("groups", { method: "GET" });
        setGroupCount(Array.isArray(grpData) ? grpData.length : 0);

        const polData = await request("policies", { method: "GET" });
        setPoliciesCount(Array.isArray(polData) ? polData.length : 0);

        const urlData = await request("urls", { method: "GET" });
        setWhitelistCount(Array.isArray(urlData) ? urlData.length : 0);
      } catch (error) {
        console.error("Error fetching counts (groups/policies/urls):", error);
      }
    };

    const fetchActiveSessions = async () => {
      try {
        const resp = await request("sessions", { method: "GET" });
        let sessions = [];
        if (Array.isArray(resp)) sessions = resp;
        else if (resp && Array.isArray(resp.data)) sessions = resp.data;
        else if (resp && Array.isArray(resp.rows)) sessions = resp.rows;
        else if (resp?.data?.docs) sessions = resp.data.docs;

        const active = sessions.filter((s) => {
          const exp = s?.expiresAt;
          return exp === null || exp === undefined || exp === "" || String(exp).toLowerCase() === "null";
        }).length;

        setActiveSessionsCount(active);
      } catch (err) {
        console.error("Error fetching sessions:", err);
        setActiveSessionsCount(0);
      }
    };

    fetchCounts();
    fetchActiveSessions();

    const interval = setInterval(fetchActiveSessions, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const reports = [
    {
      title: "Groups",
      value: groupCount,
      icon: "mdi mdi-account-group",
      iconColor: "var(--blue)",
      iconBg: "var(--tint)",
      desc: "All configured groups.",
      link: "/groups",
      sparkline: "var(--blue)",
    },
    {
      title: "Policies",
      value: policiesCount,
      icon: "mdi mdi-shield-outline",
      iconColor: "var(--secure)",
      iconBg: "rgba(0, 168, 143, 0.1)",
      desc: "Active security policies.",
      link: "/policies",
      sparkline: "var(--secure)",
    },
    {
      title: "Whitelisted URLs",
      value: whitelistCount,
      icon: "mdi mdi-link",
      iconColor: "var(--elevated)",
      iconBg: "rgba(240, 162, 2, 0.1)",
      desc: "Allowed domain lists.",
      link: "/urls",
      sparkline: "var(--elevated)",
    },
    {
      title: "Active Sessions",
      value: activeSessionsCount,
      icon: "mdi mdi-monitor",
      iconColor: "var(--critical)",
      iconBg: "rgba(214, 50, 58, 0.1)",
      desc: "Currently active sessions.",
      link: "/active-sessions",
      sparkline: "var(--critical)",
    },
  ];

  return (
    <div className="mini-widget-grid">
      {reports.map((report, idx) => (
        <Link
          to={report.link}
          key={idx}
          style={{ textDecoration: "none", display: "block" }}
        >
          <div
            className="global-card metric-card"
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              padding: "20px 24px",
              minHeight: "150px",
              borderRadius: "12px",
              border: "1px solid var(--hairline)",
              backgroundColor: "var(--canvas)",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.08)";
              e.currentTarget.style.borderColor = report.iconColor;
              e.currentTarget.querySelector('.metric-title').style.color = report.iconColor;
              e.currentTarget.querySelector('.metric-value').style.color = report.iconColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)";
              e.currentTarget.style.borderColor = "var(--hairline)";
              e.currentTarget.querySelector('.metric-title').style.color = "var(--vault-navy)";
              e.currentTarget.querySelector('.metric-value').style.color = "var(--vault-navy)";
            }}
          >
            {/* Top row: Icon and Sparkline */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: report.iconBg,
                  color: report.iconColor,
                }}
              >
                <i className={report.icon} style={{ fontSize: "20px" }} aria-hidden />
              </div>
              <svg width="60" height="20" viewBox="0 0 60 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M 2 16 Q 15 2 30 14 T 58 4" stroke={report.sparkline} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>

            {/* Middle: Title & Desc */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div 
                className="metric-title"
                style={{ 
                  margin: 0, 
                  fontSize: "15px",
                  fontWeight: 700, 
                  color: "var(--vault-navy)",
                  fontFamily: "var(--font-head)",
                  letterSpacing: "-0.2px",
                  transition: "color 0.2s ease"
                }}
              >
                {report.title}
              </div>
              <small 
                style={{ 
                  display: "block", 
                  marginTop: "4px", 
                  fontSize: "12px",
                  color: "var(--slate)",
                  lineHeight: 1.4
                }}
              >
                {report.desc}
              </small>
            </div>

            {/* Bottom: Number */}
            <div style={{ marginTop: "12px" }}>
              <div 
                className="metric-value"
                style={{ 
                  margin: 0, 
                  fontSize: "32px", 
                  color: "var(--vault-navy)", 
                  fontWeight: 700,
                  fontFamily: "var(--font-head)",
                  lineHeight: 1,
                  letterSpacing: "-0.5px",
                  transition: "color 0.2s ease"
                }}
              >
                {report.value}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>


  );
};

export default MiniWidgets;